import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";
import { adminApi, formatError } from "@/lib/adminApi";
import { tl, trDate } from "@/lib/booking";
import { SectionHeader } from "@/pages/admin/ui";
import VisitDetail from "@/pages/admin/sections/VisitDetail";

const input = "min-h-[44px] rounded-xl border border-line bg-white px-3 text-base";
const istToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }).format(new Date());
export const STATUS = { active: "Kayıtlı", checked_in: "Giriş yaptı", cancelled: "İptal", voided: "Geçersiz (gizli)" };
export const METHOD = { cash: "Nakit", card: "Kart" };
export const dt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", dateStyle: "short", timeStyle: "short" }) : "–");

function Summary({ s }) {
  if (!s) return null;
  const items = [
    ["Kayıtlı kişi", s.registered_persons], ["Giren kişi", s.entered_persons], ["Kalan kontenjan", `${s.remaining} / ${s.capacity}`],
    ["Araç (kayıtlı / giren)", `${s.vehicles_registered} / ${s.vehicles_entered}`], ["Nakit", tl(s.cash_total)], ["Kart", tl(s.card_total)],
  ];
  return (
    <div className="grid gap-3 grid-cols-2 lg:grid-cols-6 mb-6" data-testid="visits-summary">
      {items.map(([k, v]) => (
        <div key={k} className="rounded-2xl border border-line bg-white p-4">
          <p className="text-xs text-ink-muted">{k}</p>
          <p className="text-xl font-semibold text-forest tabular-nums" data-testid={`visits-summary-${k.split(" ")[0].toLowerCase()}`}>{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function VisitsSection() {
  const [date, setDate] = useState(istToday());
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [sum, setSum] = useState(null);
  const [open, setOpen] = useState(null);
  const params = { date: date || undefined, status: status || undefined, q: q.trim() || undefined };

  const load = useCallback(() => {
    adminApi.get("/admin/visits", { params: { date: date || undefined, status: status || undefined, q: q.trim() || undefined } }).then(({ data }) => setItems(data)).catch((e) => toast.error(formatError(e)));
    adminApi.get("/admin/visits/summary", { params: { date: date || istToday() } }).then(({ data }) => setSum(data)).catch(() => null);
  }, [date, status, q]);
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const csv = async () => {
    try {
      const { data } = await adminApi.get("/admin/visits.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      Object.assign(document.createElement("a"), { href: url, download: "ziyaretler.csv" }).click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const update = (b) => {
    setItems((list) => list.map((x) => (x.booking_id === b.booking_id ? b : x)));
    setOpen(b);
    load();
  };

  return (
    <section data-testid="admin-section-ziyaretler">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHeader title="Ziyaretler" text="Ön kayıtlar ve gişe satışları. Özet kartları seçilen gün içindir." />
        <button type="button" onClick={csv} className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-full border border-line bg-white text-sm font-semibold hover:bg-cream" data-testid="visits-export-csv"><Download className="h-4 w-4" aria-hidden="true" /> CSV indir</button>
      </div>
      <Summary s={sum} />
      <div className="flex flex-wrap gap-3 mb-4">
        <label className="text-sm font-semibold text-wood space-y-1">Tarih<input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${input} block`} data-testid="visits-filter-date" /></label>
        <label className="text-sm font-semibold text-wood space-y-1">Durum
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${input} block`} data-testid="visits-filter-status">
            <option value="">Tümü (geçersizler hariç)</option>
            {Object.entries(STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-wood space-y-1 flex-1 min-w-[220px]">Ara (ad, telefon, plaka, kod)
          <span className="relative block"><Search className="absolute left-3 top-3.5 h-4 w-4 text-ink-muted" aria-hidden="true" /><input value={q} onChange={(e) => setQ(e.target.value)} className={`${input} w-full pl-9`} data-testid="visits-filter-search" /></span>
        </label>
        <button type="button" onClick={() => setDate("")} className="self-end min-h-[44px] px-4 rounded-full border border-line text-sm font-semibold" data-testid="visits-all-dates">Tüm tarihler</button>
      </div>
      {items === null && <p className="text-ink-muted">Yükleniyor…</p>}
      {items?.length === 0 && <p className="rounded-2xl border border-dashed border-line bg-white p-8 text-center text-ink-muted" data-testid="visits-empty">Kayıt yok.</p>}
      {items?.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-line bg-white">
          <table className="w-full text-sm" data-testid="visits-table">
            <thead className="bg-cream-2 text-left text-xs text-ink-muted">
              <tr>{["Kod", "Tarih", "Ad soyad", "Telefon", "Y / Ç / Ü", "Plaka", "Tutar", "Ödeme", "Durum", "Giriş", "Görevli"].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((b) => (
                <tr key={b.booking_id} className="hover:bg-cream cursor-pointer" onClick={() => setOpen(b)} data-testid={`visit-row-${b.code}`}>
                  <td className="px-3 py-2 font-mono font-semibold"><button type="button" className="underline" onClick={() => setOpen(b)}>{b.code}</button></td>
                  <td className="px-3 py-2 whitespace-nowrap">{trDate(b.date)}</td>
                  <td className="px-3 py-2">{b.name}{b.source === "walkin" && <span className="ml-1 text-xs text-ink-muted">(gişe)</span>}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{b.phone || "–"}</td>
                  <td className="px-3 py-2 tabular-nums">{b.counts.adult} / {b.counts.child} / {b.counts.free}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{b.plate || "–"}</td>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">{tl(b.amount_due)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{b.payment ? `${METHOD[b.payment.method]} ${tl(b.payment.amount)}` : "–"}</td>
                  <td className="px-3 py-2 whitespace-nowrap" data-testid={`visit-row-${b.code}-status`}>{STATUS[b.status]}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{dt(b.checked_in_at)}</td>
                  <td className="px-3 py-2">{b.checked_in_by || "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <VisitDetail b={open} onClose={() => setOpen(null)} onUpdated={update} onDeleted={() => { setOpen(null); load(); }} />}
    </section>
  );
}
